import React, { useEffect, useRef, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { useChat } from '../../context/chatContext';
import { useChatSocket } from '../../hooks/useChatSocket';
import ChatInput from '../chat/chatInput';
import ThinkingStreamPanel from '../common/thinkingStreamPanel';
import ChatMessage from './chatMessage';
import type { ChatMessage as ChatMessageType } from '../../types';

// Optional: tweak this threshold as you like
const TOP_LOAD_THRESHOLD_PX = 48;

const PermanentChat: React.FC = () => {
  const {
    userId,
    token,
    activeChatId,
    isLoading,                // from context (HTTP or unified busy state)
    getVisibleMessages,       // <- from ChatContext (array of ChatMessage for active chat)
    loadOlderForActive, 
    setMessages,
    sendMessage,
    setSessionId,
    setToken      // <- from ChatContext (no-op if nothing older)
  } = useChat();

  // If user/session not ready, render nothing (keeps UI clean)
  if (!userId || !token) return <></>;

  const messages: ChatMessageType[] = getVisibleMessages();

  // WebSocket: keep streaming/UX here, but DO NOT use its messages for rendering.
  // ChatContext owns the message store.
  const {
    pending,                  // streaming in-progress indicator
    error,                    // socket error
    chatId,                   // active chat id from socket (should match activeChatId)
    //sendMessage,              // socket send (or swap for context.sendMessage if you prefer HTTP)
    deepLearning,
    toggleDeepLearning,
    clearError,
  } = useChatSocket({ userId, token, setSessionId, setToken, setMessages, messages });

  const scrollRef = useRef<HTMLDivElement>(null);
  const lastCountRef = useRef<number>(0);

  // Messages come ONLY from context

  // Load older on near-top scroll; preserve viewport
  const handleScroll = useCallback(async () => {
    const el = scrollRef.current;
    if (!el) return;

    if (el.scrollTop <= TOP_LOAD_THRESHOLD_PX) {
      const prevHeight = el.scrollHeight;

      // Ask context to extend the window upward (it will no-op if none)
      await loadOlderForActive?.();

      // Re-anchor viewport so content doesn't jump
      requestAnimationFrame(() => {
        const newHeight = el.scrollHeight;
        el.scrollTop = el.scrollTop + (newHeight - prevHeight);
      });
    }
  }, [loadOlderForActive]);

  // Bind scroll listener
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const fn = () => { handleScroll(); };
    el.addEventListener('scroll', fn, { passive: true });
    return () => el.removeEventListener('scroll', fn);
  }, [handleScroll]);

  // Auto-stick to bottom when new messages arrive and user is already at bottom
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const atBottom = Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) < 12;
    const grew = messages.length > lastCountRef.current;
    if (grew && atBottom) {
      el.scrollTop = el.scrollHeight;
    }
    lastCountRef.current = messages.length;
  }, [messages.length]);

  // Snap to bottom whenever the active chat changes
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Wait for messages to render
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [activeChatId]);

  // No chat chosen yet
  if (!activeChatId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        Select a chat or start a new one
      </Box>
    );
  }

  return (
    <>
      {/* Scroll container */}
      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: { xs: 0, sm: 2 },
          py: { xs: 1, sm: 2 },
        }}
      >
        {messages.map(m => (
          <ChatMessage key={m.id ?? m.client_message_id} message={m} />
        ))}
      </Box>

      {/* Live thinking panel (socket-driven or unify with context if you prefer) */}
      {(userId && activeChatId && token && (isLoading || pending)) && (
        <ThinkingStreamPanel
          userId={userId}
          token={token}
          chatId={activeChatId}
          defaultExpanded
          maxHeight="30vh"
          title="Enantio is thinking..."
        />
      )}

      {/* Errors */}
      {error && (
        <Box sx={{ px: 2, pb: 1, flexShrink: 0 }}>
          <Alert severity="error" onClose={clearError} variant="filled">
            {error}
          </Alert>
        </Box>
      )}

      {/* Input (uses socket send; swap to context.sendMessage if you want HTTP only) */}
      <ChatInput
        onSendMessage={sendMessage}
        isLoading={isLoading || pending}
        deepLearning={!!deepLearning}
        onToggleDeepLearning={toggleDeepLearning}
      />
    </>
  );
};

export default PermanentChat;
